import type { GuardianPlayer } from '@/features/guardian/services/get-guardian-players';
import { toProgress } from '@/features/onboarding/utils/player-card';
import { differenceInYears, isValid, parseISO } from 'date-fns';

/** A kid rendered on the Kids ("My family") screen. Name/age/level/progress/pins
 *  come from the guardian players API; only the next-session label is mocked
 *  until the session endpoint is wired up (see MOCK_NEXT_SESSION_LABELS). */
export type FamilyKid = {
  id: string;
  name: string;
  /** Whole years from `dob`; omitted when the player has no valid birth date. */
  age?: number;
  /** Pathway level (1–10); drives the badge + progress colour. */
  level: number;
  /** Progress through the current level, 0–1. */
  progress: number;
  avatar?: { uri: string };
  /** Earned pins (badges) on the current level. */
  pinsEarned: number;
  /** Total pins (badges) available on the current level. */
  pinsTotal: number;
  // ── Mocked activity (TODO: replace with session API) ──────────────────────
  /** Human label for the next scheduled session, e.g. "Tue 04:30 pm". */
  nextLabel: string;
};

/** Per-kid mock next-session label, cycled by index so each card reads
 *  differently. TODO: replace with real next-session data once the endpoint exists. */
const MOCK_NEXT_SESSION_LABELS = [
  'Tue 04:30 PM',
  'Tue 05:30 PM',
  'Wed 03:00 PM',
  'Thu 04:00 PM',
] as const;

const computeAge = (dob?: string): number | undefined => {
  if (!dob) return undefined;
  const parsed = parseISO(dob);
  if (!isValid(parsed)) return undefined;
  const age = differenceInYears(new Date(), parsed);
  return age >= 0 ? age : undefined;
};

/** Earned/total pins (badges) for the player's current level. Returns 0/0 when
 *  the level is absent or has no badges. */
const computePins = (player: GuardianPlayer): { earned: number; total: number } => {
  const current = player.progress?.find(level => level.name === `level${player.level}`);
  const badges = current?.badges ?? [];
  return {
    earned: badges.reduce((count, badge) => count + (badge.badgeEarned ? 1 : 0), 0),
    total: badges.length,
  };
};

/** Map an API player to the Kids-screen card view model. */
export const toFamilyKid = (player: GuardianPlayer, index: number): FamilyKid => {
  const pins = computePins(player);
  return {
    id: player.id,
    name: [player.firstname, player.lastname].filter(Boolean).join(' ').trim(),
    age: computeAge(player.dob),
    level: player.level,
    progress: toProgress(player),
    avatar: player.avatarUrl ? { uri: player.avatarUrl } : undefined,
    pinsEarned: pins.earned,
    pinsTotal: pins.total,
    nextLabel: MOCK_NEXT_SESSION_LABELS[index % MOCK_NEXT_SESSION_LABELS.length],
  };
};
